"use client"

import { useEffect, useCallback, useState } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Icon } from "@/shared/components/icons"
import { lockBodyScroll, unlockBodyScroll } from "@/shared/lib/body-scroll-lock"
import { ExpandableClampedText } from "@/shared/components/expandable-clamped-text"
import { ImageFallback } from "@/shared/components/ui/image-fallback"
import { cn } from "@/shared/lib/utils"

interface ImageZoomLightboxProps {
  src: string
  alt?: string
  caption?: string
  open: boolean
  onClose: () => void
}

/** Full-screen image zoom overlay — rendered via portal to document.body to escape scroll containers and stacking contexts. */
export function ImageZoomLightbox({ src, alt, caption, open, onClose }: ImageZoomLightboxProps) {
  const [mounted, setMounted] = useState(false)
  const [loadedImageSrc, setLoadedImageSrc] = useState<string | null>(null)
  const [failedImageSrc, setFailedImageSrc] = useState<string | null>(null)
  const [trackedSrc, setTrackedSrc] = useState(src)

  if (src !== trackedSrc) {
    setTrackedSrc(src)
    setLoadedImageSrc(null)
    setFailedImageSrc(null)
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener("keydown", handleEscape)
    lockBodyScroll()
    return () => {
      document.removeEventListener("keydown", handleEscape)
      unlockBodyScroll()
    }
  }, [open, handleEscape])

  const imageLoaded = loadedImageSrc === src
  const imageFailed = !src || failedImageSrc === src

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center cursor-pointer"
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image"
          onClick={onClose}
          style={{ touchAction: "none", isolation: "isolate" }}
        >
          {/* Backdrop — full cover, blur behind */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 bg-black/95 backdrop-blur-md pointer-events-none"
          />

          {/* Close button */}
          <motion.button
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 300, damping: 25 }}
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            className={cn(
              "absolute top-4 right-4 z-10 flex size-11 items-center justify-center",
              "rounded-full bg-white/10 text-white/90 backdrop-blur-sm",
              "hover:bg-white/20 hover:text-white transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-transparent"
            )}
            aria-label="Close zoom"
          >
            <Icon name="x" size={20} className="size-5" />
          </motion.button>

          {/* Image container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 25,
              mass: 0.7,
            }}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 flex w-fit cursor-default flex-col items-center justify-center"
            style={{ touchAction: "none" }}
          >
            <div className="relative flex min-h-[240px] min-w-[240px] max-w-[92vw] items-center justify-center overflow-hidden rounded-lg bg-muted/40 shadow-2xl ring-1 ring-white/5 sm:min-h-[360px] sm:min-w-[360px]">
              {imageFailed ? (
                <ImageFallback className="absolute inset-0" />
              ) : (
                <>
                  <div
                    className={cn(
                      "absolute inset-0 animate-pulse bg-muted/60 transition-opacity duration-300",
                      imageLoaded ? "opacity-0" : "opacity-100"
                    )}
                    aria-hidden="true"
                  />
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={src}
                    alt={alt ?? "Zoomed image"}
                    className={cn(
                      "max-h-[80vh] shrink-0 w-auto max-w-[92vw] object-contain transition-opacity duration-300",
                      imageLoaded ? "opacity-100" : "opacity-0"
                    )}
                    draggable={false}
                    style={{ touchAction: "none" }}
                    onLoad={() => setLoadedImageSrc(src)}
                    onError={() => setFailedImageSrc(src)}
                  />
                </>
              )}
            </div>
            {caption && caption.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 20 }}
                className="mt-4 shrink-0 max-w-md px-4 flex w-full flex-col items-center"
              >
                <ExpandableClampedText
                  text={caption}
                  lines={2}
                  className="text-center text-sm text-white/70"
                  toggleAlign="center"
                  toggleClassName="text-white/80 hover:text-white"
                />
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  // Portal to document.body so the lightbox escapes all scroll containers and stacking contexts
  if (!mounted) return null
  return createPortal(content, document.body)
}
